import { SimpleTable } from '@core/components/Table';
import { TableActionButton, TableActionButtonEdit, TableActionContainer } from '@core/components/TableAction';
import { useApiUtils } from '@core/util/apiUtils';
import { permissaoUtils } from '@core/util/permissaoUtil';
import { formatCNPJ, formatCPF } from '@format';
import { makeStyles } from '@material-ui/core';
import { confirmarAlert, excluirAlert } from 'app/services/alerts';
import empresaApi from 'app/services/api/empresaApi';
import * as Actions from 'app/store/actions';
import { GlobalContext } from 'app/store/globalcontext/GlobalContext';
import { eventReactGA } from 'app/utils/analyticsUtil';
import { getEmpresaContexto } from 'app/utils/geralUtil';
import React, { useContext } from 'react';
import { FaToggleOff, FaToggleOn } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { HTTP_STATUS } from '../../../utils/httpStatusUtil';
import { EmpresaTableContext } from '../EmpresaTableContext';
import { simpleAlert } from 'app/services/alerts';
import { EmpresaModais } from '../modal';
import EmpresaFab from './EmpresaFab';

export const tableAtivaInativaActionButtonStyles = makeStyles({
  buttonContainer: {
    color: 'rgba(88, 144, 170, 0.8)',
    borderRadius: '30%',
    padding: 4,
    border: 0,
    backgroundColor: '#f5f5f5',
    '&:hover': {
      backgroundColor: '#f5f5f5',
      boxShadow: 'none',
      borderColor: 'rgba(88, 144, 170, 0)',
    },
    '&:active': {
      boxShadow: 'none',
      backgroundColor: '#f5f5f5',
      borderColor: 'v',
    },
    '&:focus': {
      boxShadow: '0 0 0 0.2rem rgba(88, 144, 170,0.9)',
      borderColor: '#f5f5f5',
    },
  },
  tagContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  tag: {
    textOverflow: 'ellipsis',
    display: 'inline-block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  tagInativo: {
    backgroundColor: '#b53838',
    color: '#ffffff',
    margin: 0,
    marginLeft: 8,
    marginRight: 8,
    paddingLeft: 4,
    paddingRight: 4,
    paddingTop: 2,
    paddingBottom: 2,
    fontSize: 12,
    fontWeight: 600,
  },
});

const EmpresaTable = ({ fetchData, editarEmpresa, cadastrarEmpresa, buscarEmpresa }) => {
  const [empresas] = useContext(EmpresaTableContext).empresas;
  const [totalElements] = useContext(EmpresaTableContext).totalElements;
  const [, setPage] = useContext(EmpresaTableContext).page;
  const [, setSize] = useContext(EmpresaTableContext).size;
  const [, setSort] = useContext(EmpresaTableContext).sort;
  const [loading, setLoading] = useContext(EmpresaTableContext).loading;
  const [search] = useContext(EmpresaTableContext).search;
  const [, setModal] = useContext(GlobalContext).modal;
  const [, setModalContextoEmpresa] = useContext(EmpresaTableContext).modal;

  const styles = tableAtivaInativaActionButtonStyles();
  const dispatch = useDispatch();
  const apiUtils = useApiUtils(dispatch, Actions);

  const showMessage = (message, variant) => {
    dispatch(
      Actions.showMessage({
        message,
        variant,
      })
    );
  };

  const chamarModalAlterarPlano = () => {
    setModal(EmpresaModais.alteracaoPlanoComercial);
  };

  const confirmarAlteracaoPlano = () => {
    confirmarAlert({
      confirmar: chamarModalAlterarPlano,
      titulo: 'Seu plano atual não permite cadastro de mais empresas.',
      mensagem: 'Você deseja conhecer mais sobre esses planos?',
    });
  };

  const fetchAtivarInativar = (empresa) => {
    let message;

    if (empresa.ativo) {
      eventReactGA('Empresa', 'Inavitar empresa');
      message = 'Empresa inativada com sucesso';
    } else {
      eventReactGA('Empresa', 'Avitar empresa');
      message = 'Empresa ativada com sucesso';
    }

    empresaApi
      .ativarInativar(empresa.id, !empresa.ativo)
      .then((response) => {
        if (response.status === HTTP_STATUS.OK) {
          showMessage(message, 'success');
        }
      })
      .then(() => {
        fetchData(search);
      })
      .catch((error) => {
        if (error?.response?.data?.errorKey === 'empresa.limite-max') {
          confirmarAlteracaoPlano();
        } else {
          apiUtils.handleApiError(error);
        }
      });
  };

  const handlerAtivarDesativar = (empresa) => {
    const empresaIdContexto = getEmpresaContexto();

    if (empresa.id === empresaIdContexto) {
      simpleAlert({
        opcoes: {
          title:
            'Não é possível inativar a empresa enquanto ela estiver sendo usada no contexto atual. Realize a troca para outra empresa.',
        },
      });
    } else {
      excluirAlert({
        confirmar: async () => {
          fetchAtivarInativar(empresa);
        },
        opcoes: {
          title: `Você realmente deseja ${empresa.ativo ? 'inativar' : 'ativar'} ?`,
          text: '',
        },
      });
    }
  };

  const handleOpenModalVizualizar = async (empresa) => {
    setLoading(true);
    await buscarEmpresa(empresa.id);
    setModalContextoEmpresa(EmpresaModais.visualizarEmpresa);
  };

  const columns = [
    {
      name: 'pessoa.nome',
      title: 'Nome',
      align: 'left',
      sortingEnabled: true,
      getCellValue: (empresa) => {
        return (
          <div className={styles.tagContainer}>
            <span className={styles.tag}>{empresa?.pessoa?.nome}</span>
            {!empresa?.ativo ? <div className={styles.tagInativo}>Inativo</div> : ''}
          </div>
        );
      },
    },
    {
      name: 'pessoa.pessoaJuridica.razaoSocial',
      title: 'Razão social',
      align: 'left',
      sortingEnabled: true,
      getCellValue: (empresa) => empresa?.pessoa?.pessoaJuridica?.razaoSocial,
    },
    {
      name: 'pessoa.cpfCnpj',
      title: 'CPF/CNPJ',
      align: 'left',
      sortingEnabled: true,
      getCellValue: (empresa) => {
        if (empresa?.pessoa?.tipoPessoa === 'JURIDICA') {
          return formatCNPJ(empresa?.pessoa?.pessoaJuridica?.cnpj);
        } else if (empresa?.pessoa?.tipoPessoa === 'FISICA') {
          return formatCPF(empresa?.pessoa?.pessoaFisica?.cpf);
        }
      },
    },
    {
      name: '',
      title: '',
      sortingEnabled: false,
      align: 'center',
      getCellValue: (empresa) => (
        <TableActionContainer>
          {permissaoUtils.minhaEmpresa().editar && <TableActionButtonEdit onClick={() => editarEmpresa(empresa.id)} />}
          {permissaoUtils.minhaEmpresa().editar && (
            <TableActionButton
              title={empresa.ativo ? 'Inativar' : 'Ativar'}
              icon={empresa.ativo ? <FaToggleOn size={18} /> : <FaToggleOff size={18} />}
              style={styles.buttonContainer}
              onClick={() => {
                handlerAtivarDesativar(empresa);
              }}
            />
          )}
        </TableActionContainer>
      ),
    },
  ];

  return (
    <>
      <SimpleTable
        hideSelection
        fetchOnCreate={false}
        defaultSorting={[{ columnName: 'pessoa.nome', direction: 'asc' }]}
        columns={columns}
        rows={empresas}
        loading={loading}
        onRowClick={handleOpenModalVizualizar}
        totalCount={totalElements}
        fetchData={fetchData}
        onCurrentPageChange={setPage}
        onPageSizeChange={setSize}
        onSortingChange={setSort}
      />
      {permissaoUtils.minhaEmpresa().cadastrar && <EmpresaFab cadastrarEmpresa={cadastrarEmpresa} />}
    </>
  );
};

export default EmpresaTable;
